<template>
	<div class="page-container">
		<!-- Banner Section -->
		<div class="banner" @mouseenter="stopAutoPlay" @mouseleave="startAutoPlay">
			<div class="banner-track" :style="bannerTrackStyle">
				<div class="banner-slide" v-for="(item, index) in banners" :key="item.id || index"
					:style="{ backgroundImage: item.imgUrl ? `url(${item.imgUrl})` : (item.background || '') }"
					@click="handleBannerClick(item)">
				</div>
			</div>

			<!-- Dots -->
			<div class="banner-dots">
				<span v-for="(item, index) in banners" :key="index" class="dot"
					:class="{ active: currentBannerIndex === index }" @click="setBanner(index)"></span>
			</div>
		</div>

		<div class="course-center">
			<!-- Left Sidebar: Category Menu -->
			<div class="category-sidebar">
				<ul class="category-list">
					<li v-for="(category, index) in categories" :key="index" class="category-item"
						:class="{ active: currentCategoryIndex === index }"
						@click="handleCategoryClick(category, index)">
						<span>{{ category.name || category }}</span>
						<span class="arrow">></span>
					</li>
				</ul>
			</div>

			<!-- Right Content: Course Grid -->
			<div class="course-content">
				<div class="course-grid" v-if="courseList && courseList.length > 0">
					<div class="course-card" v-for="(course,index) in courseList" :key="index"
						@click="goToDetail(course)">
						<div class="course-cover">
							<img :src="course.coverImg" alt="" v-if="course.coverImg">
							<span class="badge" :class="getBadgeType(course.viewType)">{{ getBadgeText(course.viewType)
								}}</span>
						</div>
						<div class="course-info">
							<h4>{{ course.title }}</h4>
							<p>{{ course.sales}}人浏览</p>
						</div>
					</div>
				</div>
				<EmptyState v-else description="暂无课程数据" />
			</div>
		</div>
	</div>
</template>

<script>
	import ProductAPI from '@/api/product'
	import EmptyState from '@/components/EmptyState.vue'

	export default {
		name: 'CourseCenter',
		components: {
			EmptyState
		},
		data() {
			return {
				currentCategory: 0,
				currentBannerIndex: 0,
				autoPlayInterval: null,
				banners: [],
				categories: [],
				courseList: [],
				currentCategoryIndex: 0,
				queryParams: {
					pageNum: 1,
					pageSize: 12,
					categoryId: null
				}
			}
		},
		computed: {
			bannerTrackStyle() {
				return {
					transform: `translateX(-${this.currentBannerIndex * 100}%)`
				}
			}
		},
		watch: {
			'$route.query.categoryId': {
				handler(newVal) {
					if (this.categories && this.categories.length > 0) {
						let targetIndex = 0
						if (newVal) {
							const index = this.categories.findIndex(c => c.categoryId == newVal || c.id == newVal)
							if (index !== -1) {
								targetIndex = index
							}
						}
						// Avoid re-fetching if already selected (optional optimization, but simple logic is safer)
						if (this.currentCategoryIndex !== targetIndex) {
							this.handleCategoryClick(this.categories[targetIndex], targetIndex)
						}
					}
				},
				immediate: false // already handled in mounted via fetchCategories
			}
		},
		mounted() {
			this.fetchBanners();
			this.fetchCategories();
		},
		beforeUnmount() {
			this.stopAutoPlay();
		},
		methods: {
			goToDetail(course) {
				if (course && (course.id || course.courseId)) {
					this.$router.push({
						name: 'CourseDetail',
						params: {
							id: course.id || course.courseId
						}
					})
				}
			},
			handleBannerClick(item) {
				console.log('Banner item clicked:', item);
				if (!item) {
					console.warn('Banner item is null or undefined');
					return;
				}

				// Ensure jumpType is treated as a string for comparison
				const jumpType = String(item.jumpType);
				
				// 尝试获取跳转内容，兼容多个可能的字段名
				// 参考 HelloWorld.vue 中的逻辑
				const jumpContent = item.jumpContent || item.jumpUrl || item.targetId || item.link || item.url;

				console.log(`Processing jump: type=${jumpType}, content=${jumpContent}`);

				// jumpType: 1是课程id
				if (jumpType === '1') {
					if (jumpContent) {
						console.log('Navigating to CourseDetail with ID:', jumpContent);
						this.$router.push({
							name: 'CourseDetail',
							params: {
								id: jumpContent
							}
						}).catch(err => {
							console.error('Router navigation failed:', err);
						});
					} else {
						console.warn('Jump type is 1 (Course), but content (ID) is missing');
					}
				} 
				// jumpType: 2是web链接
				else if (jumpType === '2') {
					if (jumpContent) {
						console.log('Opening external link:', jumpContent);
						// 检查是否包含 http/https 前缀
						let url = jumpContent;
						if (!url.startsWith('http://') && !url.startsWith('https://')) {
							url = 'http://' + url;
						}
						window.open(url, '_blank');
					} else {
						console.warn('Jump type is 2 (Web), but content (URL) is missing');
					}
				} 
				else {
					console.warn('Unknown jumpType:', jumpType);
				}
			},
			async fetchBanners() {
				try {
					const res = await ProductAPI.getBanners();
					if (res && res.length > 0) {
						this.banners = res;
						this.currentBannerIndex = 0;
					}
					this.startAutoPlay();
				} catch (e) {
					console.error(e);
					this.startAutoPlay();
				}
			},
			async fetchCategories() {
				try {
					const res = await ProductAPI.getCategories()
					this.categories = res || []
					if (this.categories && this.categories.length > 0) {
						// 检查路由参数是否有 categoryId
						const queryCategoryId = this.$route.query.categoryId
						let targetIndex = 0

						if (queryCategoryId) {
							const index = this.categories.findIndex(c => c.categoryId == queryCategoryId || c.id ==
								queryCategoryId)
							if (index !== -1) {
								targetIndex = index
							}
						}

						this.handleCategoryClick(this.categories[targetIndex], targetIndex)
					} else {
						// 即使没有分类，也尝试加载一次课程（可能显示全部）
						this.fetchCourses()
					}
				} catch (e) {
					console.error(e)
					this.fetchCourses()
				}
			},
			handleCategoryClick(category, index) {
				this.currentCategoryIndex = index
				// 优先使用 categoryId，如果不存在尝试使用 id
				const id = category.categoryId !== undefined ? category.categoryId : category.id
				this.queryParams.categoryId = id !== undefined ? id : null
				this.queryParams.pageNum = 1
				this.fetchCourses()
			},
			async fetchCourses() {
				try {
					const params = {
						...this.queryParams,
						page: this.queryParams.pageNum
					}
					const res = await ProductAPI.getCourseList(params)
					console.log('Course list response:', res)

					if (res && Array.isArray(res.rows)) {
						this.courseList = res.rows
					} else if (res && res.data && Array.isArray(res.data.rows)) {
						this.courseList = res.data.rows
					} else if (res && Array.isArray(res.list)) {
						this.courseList = res.list
					} else if (Array.isArray(res)) {
						this.courseList = res
					} else if (res && res.data && Array.isArray(res.data)) {
						this.courseList = res.data
					} else {
						this.courseList = []
						console.warn('Unknown course list response format', res)
					}

					console.log('Final courseList:', this.courseList)
				} catch (e) {
					console.error('Fetch courses error:', e)
					this.courseList = []
				}
			},
			nextBanner() {
				this.currentBannerIndex = (this.currentBannerIndex + 1) % this.banners.length;
			},
			prevBanner() {
				this.currentBannerIndex = (this.currentBannerIndex - 1 + this.banners.length) % this.banners.length;
			},
			setBanner(index) {
				this.currentBannerIndex = index;
			},
			startAutoPlay() {
				if (this.autoPlayInterval) return;
				this.autoPlayInterval = setInterval(this.nextBanner, 3000); // Change every 3 seconds
			},
			stopAutoPlay() {
				if (this.autoPlayInterval) {
					clearInterval(this.autoPlayInterval);
					this.autoPlayInterval = null;
				}
			},
			getBadgeType(viewType) {
				// 1:免费, 2:VIP免费, 3:付费
				if (viewType === 2) return 'vip';
				if (viewType === 3) return 'paid';
				return 'free';
			},
			getBadgeText(viewType) {
				// 1:免费, 2:VIP免费, 3:付费
				if (viewType === 2) return 'VIP';
				if (viewType === 3) return '付费';
				return '免费';
			},
		}
	}
</script>

<style scoped>
	.page-container {
		padding: 0;
		max-width: 100%;
		min-height: 80vh;
		color: #fff;
		background-color: #121212;
		/* Overall dark background */
	}

	/* Banner Styles - Refactored based on HelloWorld.vue */
	.banner {
		width: 100%;
		height: 400px;
		margin-bottom: 30px;
		position: relative;
		overflow: hidden;
		background: linear-gradient(to right, #1e1e2d, #2a2a3d);
	}

	.banner-track {
		display: flex;
		height: 100%;
		transition: transform 0.5s ease;
	}

	.banner-slide {
		min-width: 100%;
		height: 100%;
		background-size: cover;
		background-position: center;
		display: flex;
		justify-content: center;
		align-items: center;
		position: relative;
		cursor: pointer;
		z-index: 1; /* Ensure it is clickable */
	}

	/* Dots */
	.banner-dots {
		position: absolute;
		bottom: 20px;
		left: 50%;
		transform: translateX(-50%);
		display: flex;
		gap: 8px;
		z-index: 10;
	}

	.dot {
		width: 30px;
		height: 4px;
		border-radius: 2px;
		background-color: rgba(255, 255, 255, 0.4);
		cursor: pointer;
		transition: all 0.3s;
	}

	.dot.active {
		background-color: #fff;
	}

	.course-center {
		display: flex;
		gap: 30px;
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px 40px 20px;
	}

	/* Sidebar Styles */
	.category-sidebar {
		width: 200px;
		flex-shrink: 0;
		background-color: #1e1e1e;
		border-radius: 4px;
		padding: 10px 0;
		height: fit-content;
	}

	.category-list {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.category-item {
		padding: 15px 20px;
		cursor: pointer;
		display: flex;
		justify-content: space-between;
		align-items: center;
		color: #999;
		transition: all 0.3s;
		font-size: 14px;
	}

	.category-item:hover {
		background-color: #2a2a2a;
		color: #fff;
	}

	.category-item.active {
		color: #ff9900;
		background-color: #2a2a2a;
	}

	.arrow {
		font-family: consolas;
		font-size: 12px;
	}

	/* Course Grid Styles */
	.course-content {
		flex: 1;
	}

	.course-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 20px;
	}

	.course-card {
		background-color: transparent;
	}

	.course-cover {
		height: 180px;
		background-color: #333;
		border-radius: 8px;
		margin-bottom: 12px;
		position: relative;
		overflow: hidden;
		/* Placeholder for actual image */
		background: linear-gradient(45deg, #2a2a2a 25%, #333 25%, #333 50%, #2a2a2a 50%, #2a2a2a 75%, #333 75%, #333 100%);
		background-size: 20px 20px;
	}

	.course-cover img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.3s;
	}

	.course-card:hover .course-cover img {
		transform: scale(1.05);
	}

	.course-info h4 {
		margin: 0 0 8px 0;
		font-size: 14px;
		font-weight: normal;
		line-height: 1.4;
		height: 40px;
		/* Limit to 2 lines approximately */
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	.course-info p {
		margin: 0;
		font-size: 12px;
		color: #666;
	}

	/* Badges */
	.badge {
		position: absolute;
		top: 0px;
		right: 0px;
		padding: 2px 8px;
		border-radius: 4px;
		font-size: 12px;
		font-weight: bold;
		color: #522606;
		background: linear-gradient(90deg, #FFC972 0%, #FFA73B 100%);
	}
</style>